import { SegmentedControl } from '@components/ui';
import { getLegendByDeclension } from '@lib/utils/graphs-utils';
import { actions as lifeIdActions } from '@store/ducks/life-id';
import { actions } from '@store/ducks/vital-history';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { IDateRange, TDetailTabs, TVitalRecordType } from 'types/life-id';
import TStore from 'types/store';
import { TTimelineRecord } from 'types/vital-data';
import { TVitalHistoryMeta } from 'types/vital-history';
import { HistoryRangePicker } from '../history-range-picker';
import StyledText from './../styled-text';
import Chart from './chart';
import { LifeChartSC, VitalLegendDotSC } from './life-chart.styled';
import getPayloadMap from './payload-map';
import useChartData from './utils/use-chart-data';

interface IProps {
  recordType: TVitalRecordType;
  dateRange: IDateRange;
  onRangeChange: React.Dispatch<React.SetStateAction<IDateRange>>;
  onHeaderChange: (height: number) => void;
}

const TABS = [
  { label: 'life_id.vitalDetail.periods.day', value: 'day', key: 'day' },
  { label: 'life_id.vitalDetail.periods.week', value: 'week', key: 'week' },
  { label: 'life_id.vitalDetail.periods.month', value: 'month', key: 'month' },
  { label: 'life_id.vitalDetail.periods.year', value: 'year', key: 'year' },
] as const;

const LifeChart: React.FC<IProps> = ({
  recordType,
  dateRange,
  onRangeChange,
  onHeaderChange,
}: IProps) => {
  const { userId } = useParams<{ userId: string; jabberId: string }>();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const providerId = useSelector((state: TStore) => state.users.business[0].id);
  const meta: TVitalHistoryMeta | null = useSelector((store: TStore) => store.vitalHistory.meta);

  const data = useSelector<TStore, TTimelineRecord>(
    (state) => state.vitalHistory.timelineData[recordType],
  );

  const { typeData } = useChartData(recordType, data);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('month');
  const mappedTabs = React.useMemo(
    () => TABS.map((t) => ({ ...t, label: formatMessage({ id: t.label }) })),
    [formatMessage],
  );

  const payloadMap = useMemo(
    () => getPayloadMap(dateRange, recordType, userId, providerId),
    [recordType, userId, dateRange, providerId],
  );

  /**
   * Function to handle periods changes
   */
  useEffect(() => {
    dispatch(
      actions.getTimelineRequest({
        dateRange,
        ...payloadMap.get(selectedPeriod as TDetailTabs),
      }),
    );
  }, [payloadMap, selectedPeriod, dispatch, dateRange]);

  if (meta === null) {
    return null;
  }

  return (
    <LifeChartSC onLayout={(e) => onHeaderChange(e.nativeEvent.layout.height)}>
      <HistoryRangePicker
        onSubmit={(args) => {
          onRangeChange(args);
          dispatch(lifeIdActions.resetVitalItems());
        }}
      />
      <SegmentedControl
        options={mappedTabs}
        value={selectedPeriod}
        onChange={setSelectedPeriod}
        variant="on-dark"
        style={{ marginTop: 16, marginBottom: 8 }}
      />
      <div className="lc-chart">
        <Chart
          data={typeData.history}
          type={recordType}
          legends={typeData.legends}
          timeLineRecord={data}
        />
      </div>
      {typeData.legends.map((legend, i) => {
        return (
          <div className="lc-legend-item" key={i}>
            <VitalLegendDotSC color={legend.color || theme.color.solid.info} />
            <StyledText>
              <FormattedMessage id={getLegendByDeclension(legend)} /> {legend.text}
            </StyledText>
          </div>
        );
      })}
    </LifeChartSC>
  );
};

export default LifeChart;
